import { createCdnThumbnailUrl, CDN_BASE_URL } from '../config/cdn';
import { Field } from './types';

// Which (entity, column) pairs hold an image, and how to resolve a URL for it.
//  - 'thumb'   → product thumbnails:  thumbnails/36_<size>/<value>.png   (value has NO extension)
//  - 'texture' → raw render textures: textures/72/<value>               (value INCLUDES extension)
// Verified against the live CDN (cdn.e-asztalos.hu) 2026-06-17.
type Kind = 'thumb' | 'texture';

const MAP: Record<string, Record<string, Kind>> = {
    decor:            { product_image: 'thumb' },
    board:            { ProductImage: 'thumb' },
    edge:             { ProductImage: 'thumb' },
    material_diffuse: { name: 'texture' },
    material_surface: {
        name_alpha: 'texture', name_normal: 'texture', name_roughness: 'texture',
        name_bump: 'texture', name_metallic: 'texture', name_emissive: 'texture',
    },
};

// Entities whose primary list thumbnail is the backend-computed __thumb (a diffuse texture path)
// rather than one of their own columns. See THUMB_EXPR in AdminUCMaterialCatalog.php.
const SYNTH_THUMB: Record<string, true> = { material: true, material_surface: true };

export function imageKind(entity: string, field: string): Kind | null {
    if (field === '__thumb') return 'texture';
    return MAP[entity]?.[field] ?? null;
}

// The CDN only generates the 36_256 thumbnail size (36_64/128/512 are 404), so always request 256
// for product thumbnails; the <img> is CSS-sized down. Textures use the size-independent 72-dpi path.
export function imageUrl(entity: string, field: string, value: unknown): string {
    if (value == null || value === '') return '';
    const kind = imageKind(entity, field);
    if (!kind) return '';
    const v = String(value).replace(/^\/+/, '');
    if (kind === 'thumb') return createCdnThumbnailUrl(v, 256);
    return `${CDN_BASE_URL}/textures/72/${v}`;
}

/** Primary image column for the list's leading thumbnail (synthetic __thumb, or first image column). */
export function primaryImageField(entity: string, fields: Field[]): string | null {
    if (SYNTH_THUMB[entity]) return '__thumb';
    const m = MAP[entity];
    if (!m) return null;
    const f = fields.find((x) => x.name in m);
    return f ? f.name : null;
}
